"use client";

import { useAppSelector } from "@/lib/store";
import { useLoadingState } from "./useLoadingState";
import { logError } from "@/lib/utils/errors/logger";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { checkVehicleSchema } from "@/lib/utils/schemas";
import { add_vehicle, registration_lookup } from "@/lib/gtm";
import { useEffect, useMemo, useRef, useState } from "react";
import { updateVehicle } from "@/lib/store/reducers/vehicle";
import { replaceHistoryState } from "@/lib/utils/browserMethods";
import { updateManualSessionId } from "@/lib/store/reducers/misc";
import { fetchMakeOptions } from "@/lib/store/reducers/makeOptions";
import { getVehicleInfo, handleVehicleResponse } from "@/lib/services/api";
import { getModelList, getVehicleNextOptions } from "@/lib/services/vehicles";
import {
  MVLPayloadToKey,
  handleToastError,
  MVLKeyToQueryString,
} from "@/lib/utils/functions";
import { addVehicleInGarage } from "../services/garage";

export const useManualVehicle = ({
  t,
  customerId,
  selectedMake,
  selectedModel,
  isGlobalForm = false,
  manualSearch = false,
  isManualForm = false,
  hasManualLook = false,
  updatePageTitle = () => {},
  removeManualSelection = () => {},
}) => {
  const modelOptions = useRef([]);
  const { dispatch, showLoader, hideLoader } = useLoadingState();
  const { makeOptions } = useAppSelector((state) => state.makeOptions);

  const [isDisabled, setIsDisabled] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchedItems, setSearchedItems] = useState([]);
  const [optionsList, setOptionsList] = useState({
    make: [],
    year: [],
    body: [],
    kw_hp: [],
    model: [],
    engine: [],
  });

  const { reset, control, register, setValue, getValues, handleSubmit } =
    useForm({
      resolver: yupResolver(checkVehicleSchema(() => {})),
      defaultValues: {
        reg: "",
        make: hasManualLook ? selectedMake?.value || "" : "",
        model: "",
        type: "",
        year: "",
        engine: "",
        body: "",
        kw_hp: "",
      },
    });

  const modelField = useWatch({ control, name: "model" });
  const hasEngineOpts = useMemo(() => {
    const yearValue = getValues("year");
    if (!yearValue) return true;
    if (isFetching) return true;
    return optionsList.engine.length > 0;
  }, [isFetching, optionsList.engine]);

  const searchItemList = useMemo(() => {
    return { items: searchedItems, hasItems: searchedItems.length > 0 };
  }, [searchedItems]);

  useEffect(() => {
    if (!modelField) {
      setValue("type", "");
    }
  }, [modelField]);

  const resetFields = (field) => {
    const fieldArr = [];
    switch (field) {
      case "body":
        fieldArr.push("kw_hp");
        break;
      case "engine":
        fieldArr.push("kw_hp", "body");
        break;
      case "year":
        fieldArr.push("kw_hp", "body", "engine");
        break;
      case "model":
        fieldArr.push("kw_hp", "body", "engine", "year");
        break;
      case "all":
        fieldArr.push(
          "kw_hp",
          "body",
          "engine",
          "year",
          "model",
          "type",
          "make",
          "reg",
        );
        break;
      default:
        fieldArr.push("kw_hp", "body", "engine", "year", "model", "type");
        break;
    }

    fieldArr.forEach((f) => {
      setValue(f, "");
    });
    setIsDisabled(true);
  };

  const resetOptionsList = (opts = {}) => {
    setOptionsList((prev) => ({
      make: opts?.make || prev.make,
      model: opts?.model || [],
      year: [],
      body: [],
      kw_hp: [],
      engine: [],
    }));
  };

  const resetFormData = () => {
    setSearchedItems([]);
    removeManualSelection();
    resetFields("all");
    resetOptionsList();
  };

  const getNextOptions = (field) => {
    let nextField = "";
    switch (field) {
      case "model":
        nextField = "year";
        break;
      case "year":
        nextField = "engine";
        break;
      case "engine":
        nextField = "body";
        break;
      case "body":
        nextField = "kw_hp";
        break;
    }
    return nextField;
  };

  const updateModalType = ({
    optList,
    selected,
    payload = {},
    setModel = false,
  }) => {
    const modelObj = optList.find((r) => r.value === selected);
    if (setModel) {
      setValue("model", selected);
    }
    if (!payload?.type) {
      payload.type = modelObj?.type || "LCV";
    }
    setValue("type", payload.type);
    return payload;
  };

  const handleMakeChange = async (make) => {
    setIsFetching(true);
    const models = await getModelList(make, t);
    resetOptionsList({ model: models });

    modelOptions.current = models;
    if (hasManualLook && selectedModel?.value) {
      updateModalType({
        setModel: true,
        optList: models,
        selected: selectedModel.value,
      });
      handleDropdownSelection("model");
    }
    setIsFetching(false);
  };

  const handleOtherFieldChange = async (field, rest) => {
    if (hasManualLook) showLoader();
    let payload = { ...rest };
    if (rest.model && !rest.type) {
      payload = updateModalType({
        payload,
        optList: modelOptions.current,
        selected: rest.model,
      });
    }

    setIsFetching(true);
    const options = await getVehicleNextOptions(t, payload, field);
    if (options?.sessionId) {
      dispatch(updateManualSessionId(options?.sessionId));
      hideLoader();
    }

    if (options?.search_now) {
      setIsDisabled(false);
      findManually();
    } else if (options?.extra_fields) {
      setIsFetching(true);
      for (const fieldIndex in options.extra_fields) {
        const valObj = options.extra_fields[fieldIndex];
        if (valObj) {
          setOptionsList((prev) => ({
            ...prev,
            [fieldIndex]: [
              {
                value: valObj?.value || valObj?.id,
                label: valObj?.label || valObj?.title,
              },
            ],
          }));
          setValue(fieldIndex, valObj?.value || valObj?.id);
        }
      }

      if (options.extra_fields?.kw_hp) {
        setIsDisabled(false);
        findManually();
      }
    } else {
      setOptionsList((prev) => {
        switch (field) {
          case "year":
            return { ...prev, engine: options.engine, body: [], kw_hp: [] };
          case "engine":
            return { ...prev, body: options.body, kw_hp: [] };
          case "body":
            return { ...prev, kw_hp: options.kw_hp };
          default:
            return {
              ...prev,
              year: options.year,
              body: [],
              kw_hp: [],
              engine: [],
            };
        }
      });
      const nextField = getNextOptions(field);
      if (nextField && options[nextField] && options[nextField]?.length === 1) {
        setValue(nextField, options[nextField][0].value);
        if (nextField === "kw_hp") {
          setIsDisabled(false);
          setIsFetching(false);
          if (!manualSearch) {
            fetchDetailByManual();
          }
          return;
        }
        handleDropdownSelection(nextField);
        setIsFetching(false);
        return;
      }
    }
    setIsFetching(false);
  };

  const handleDropdownSelection = async (field) => {
    resetFields(field);
    try {
      const { reg, ...rest } = getValues();
      if (field === "make") {
        await handleMakeChange(rest.make);
      } else {
        await handleOtherFieldChange(field, rest);
        hideLoader();
      }
    } catch (err) {
      hideLoader();
      handleToastError(err?.message);
    }
  };

  const sendGTMData = (vehicleData, isRegLookup = false) => {
    let gtagData = {
      model: vehicleData?.model,
      manufacturer: vehicleData?.make,
    };

    if (isRegLookup) {
      gtagData.engine = vehicleData?.engineSize;
      gtagData.years = vehicleData?.dateFirstRegistered?.match(/^(\d{4})/)[0];
    } else {
      gtagData.years = vehicleData?.modelVersion?.match(/\d{4}/)[0];
      if (vehicleData?.engineSize) {
        gtagData.engine = vehicleData?.engineSize;
      }
    }
    registration_lookup(gtagData);
  };

  const findUsingReg = async (formData) => {
    const regNumber = formData?.reg?.replace(/\s/g, "");
    if (!regNumber) {
      handleToastError(t("messages.error.required_vehicle_registration"));
      return;
    }

    setIsFetching(true);
    if (isGlobalForm) {
      const ele = document.querySelector("#current-vehicle");
      if (ele) {
        ele.classList.remove("border-danger");
      }
    }

    try {
      const vRes = await getVehicleInfo(`vrm=${regNumber}`, true);
      const vData = handleVehicleResponse(t, vRes, handleToastError);
      if (!vData) return;

      dispatch(updateVehicle(vData));
      if (isGlobalForm) {
        updatePageTitle();
        const query = new URLSearchParams(window.location.search);
        const { make, model, ...params } = Object.fromEntries(query.entries());
        if (make && model) {
          const searchQueryStr = new URLSearchParams(params).toString();
          replaceHistoryState({ url: `?${searchQueryStr}` });
        }
      }

      reset();
      sendGTMData(vData?.vehicle || {}, true);
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
      logError({ error, info: { regNo }, message: "Error in searchUsingReg" });
      handleToastError(error.message);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchDetailByManual = async () => {
    setIsFetching(true);
    let { reg, ...payload } = getValues();

    try {
      const queryStr = MVLKeyToQueryString(MVLPayloadToKey(payload));
      const vRes = await getVehicleInfo(queryStr, true, hasManualLook);
      const vData = handleVehicleResponse(t, vRes, handleToastError);
      if (!vData) return;
      dispatch(updateVehicle(vData));
      dispatch(updateManualSessionId(null));
      sendGTMData(vData?.vehicle || {});

      const urlSearchParams = new URLSearchParams(window.location.search);
      const { make, model, ...params } = Object.fromEntries(
        urlSearchParams.entries(),
      );
      const searchParamString = new URLSearchParams(params).toString();
      replaceHistoryState({ url: `?${searchParamString}` });
      resetOptionsList();
      reset();
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
      logError({
        error,
        info: { ...payload },
        message: "Error in findManually",
      });
      handleToastError(err.message);
    } finally {
      setIsFetching(false);
    }
  };

  const findManually = async () => {
    if (manualSearch) {
      setIsDisabled(false);
    } else {
      fetchDetailByManual();
    }
  };

  const searchVehicle = async (formData) => {
    setSearchedItems([]);
    setIsFetching(true);
    const { reg, ...payload } = formData;
    const cleanedReg = reg?.replace(/\s/g, "");
    if (!isManualForm && !reg) {
      setIsFetching(false);
      handleToastError(t("messages.error.required_vehicle_registration"));
      return;
    }

    try {
      let vRes = null;
      if (reg) {
        vRes = await getVehicleInfo(`vrm=${cleanedReg}`);
      } else {
        const queryString = MVLKeyToQueryString(MVLPayloadToKey(payload));
        vRes = await getVehicleInfo(queryString);
      }

      const vData = handleVehicleResponse(t, vRes, handleToastError);
      if (!vData) return;
      setSearchedItems([vData]);
    } catch (error) {
      setIsFetching(false);
      handleToastError(error.message);
      logError({
        error,
        info: { ...formData },
        message: "Error in searchVehicle",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const addVehicleToGarage = async (resData) => {
    setIsFetching(true);

    const { vehicle, session } = resData;
    let payload = {
      customer_id: customerId,
      session_id: session.sessionId,
      data: JSON.stringify(vehicle),
    };

    if (vehicle?.vrm) {
      payload.vrm = vehicle.vrm;
      payload.is_mvl = false;
    } else {
      payload.vrm = vehicle.gCodePartialIDs.join("_");
      payload.is_mvl = true;
    }

    try {
      const { data, error } = await addVehicleInGarage(payload);
      if (error) {
        handleToastError(error);
        logError({
          error,
          info: { ...resData },
          message: "Error in addVehicleToGarage",
        });
        return;
      }

      if (data?.addGsfMyGarageRecord?.success === false) {
        handleToastError(data?.addGsfMyGarageRecord?.message);
        logError({
          isWarning: true,
          message: "Error in addVehicleToGarage",
          info: { ...resData, resErr: data?.addGsfMyGarageRecord },
        });
        return;
      }

      // Send GTM data for vehicle added to garage event
      add_vehicle({
        manufacturer: vehicle?.make,
        model: vehicle?.model,
        years:
          vehicle?.modelVersion?.match(/\d{4}/)[0] ||
          vehicle?.dateFirstRegistered?.match(/^(\d{4})/)[0],
        ...(vehicle?.engineSize && { engine: vehicle?.engineSize }),
      });

      setShowSuccess((prev) => !prev);
    } catch (error) {
      setIsFetching(false);
      handleToastError(error.message);
      logError({
        error,
        info: { ...resData },
        message: "Error in addVehicleToGarage ::479",
      });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (isManualForm && !makeOptions.length) {
      setIsFetching(true);
      dispatch(fetchMakeOptions()).then(({ meta, payload }) => {
        resetOptionsList({
          make: meta?.requestStatus === "fulfilled" ? payload : [],
        });
        setIsFetching(false);
      });
    } else {
      resetOptionsList({ make: makeOptions });
    }
  }, [dispatch, isManualForm, makeOptions]);

  useEffect(() => {
    if (hasManualLook && selectedMake?.value) {
      handleDropdownSelection("make");
    }
  }, [hasManualLook, selectedMake]);

  useEffect(() => {
    return () => {
      reset();
      resetOptionsList();
    };
  }, [reset]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (hasManualLook && (!search.has("make") || !search.has("model"))) {
      removeManualSelection();
      resetFields("all");
      resetOptionsList();
    }
  }, []);

  return {
    data: {
      control,
      isFetching,
      isDisabled,
      optionsList,
      showSuccess,
      hasEngineOpts,
      searchItemList,
    },
    methods: {
      reset,
      register,
      findUsingReg,
      findManually,
      handleSubmit,
      resetFormData,
      searchVehicle,
      setShowSuccess,
      resetOptionsList,
      setSearchedItems,
      addVehicleToGarage,
      fetchDetailByManual,
      handleDropdownSelection,
    },
  };
};
